import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import SEO from "../components/SEO";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Phone, Mail, MapPin, MessageCircle, Clock, Send, Zap, Camera, X } from "lucide-react";
import { compressImage, MAX_UPLOAD_BYTES, formatBytes } from "../lib/compressImage";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';
const MAX_PHOTOS = 5;

const services = [
  "Aannemer / Complete renovatie",
  "Badkamer renovatie",
  "Stukadoor werk",
  "Schilderwerk",
  "Timmerwerk",
  "Werk op uurtarief",
  "Anders",
];

const requiredFields = [
  ["name", "naam"],
  ["email", "e-mailadres"],
  ["phone", "telefoonnummer"],
  ["address", "adres"],
  ["service", "dienst"],
  ["preferred_date", "gewenste datum"],
];

const inputClass = "h-12 border-slate-300 focus:border-[#005F5F] focus:ring-[#005F5F] rounded-none";

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    service: "",
    preferred_date: "",
    is_spoed: false,
    message: "",
    photos: [],
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState("");
  const [photoFiles, setPhotoFiles] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handlePhotoSelect = async (e) => {
    const selected = Array.from(e.target.files);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (selected.length === 0) return;

    if (photoFiles.length + selected.length > MAX_PHOTOS) {
      toast.error(`Maximaal ${MAX_PHOTOS} foto's toegestaan`);
      return;
    }

    const tooLarge = selected.filter((f) => f.size > MAX_UPLOAD_BYTES);
    if (tooLarge.length > 0) {
      toast.error(`${tooLarge.map((f) => f.name).join(", ")}: groter dan 50MB`);
      return;
    }

    setUploading(true);
    try {
      setUploadStatus("Foto's verkleinen...");
      const prepared = await Promise.all(selected.map((f) => compressImage(f)));

      setUploadStatus("Foto's uploaden...");
      const body = new FormData();
      prepared.forEach((file) => body.append('files', file));
      const response = await axios.post(`${API}/upload-multiple`, body, { timeout: 120000 });

      const uploaded = response.data.uploaded || [];
      const errors = response.data.errors || [];
      const failedNames = new Set(errors.map((er) => er.filename));
      const okFiles = prepared.filter((f) => !failedNames.has(f.name));

      const newEntries = uploaded.map((u, i) => ({
        name: okFiles[i]?.name || u.filename,
        url: u.url,
        size: u.size,
        preview: okFiles[i] ? URL.createObjectURL(okFiles[i]) : `${process.env.REACT_APP_BACKEND_URL || ''}${u.url}`,
      }));

      setFormData((prev) => ({ ...prev, photos: [...prev.photos, ...uploaded.map((u) => u.url)] }));
      setPhotoFiles((prev) => [...prev, ...newEntries]);

      if (uploaded.length > 0) toast.success(`${uploaded.length} foto('s) toegevoegd`);
      errors.forEach((er) => toast.error(`${er.filename}: ${er.reason}`));
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || "Foto upload mislukt. Controleer uw verbinding en probeer opnieuw.");
    } finally {
      setUploading(false);
      setUploadStatus("");
    }
  };

  const removePhoto = (index) => {
    setFormData((prev) => ({ ...prev, photos: prev.photos.filter((_, i) => i !== index) }));
    setPhotoFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const missing = requiredFields.filter(([key]) => !formData[key]).map(([, label]) => label);
    if (missing.length > 0) {
      toast.error(`Vul nog in: ${missing.join(", ")}.`);
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error("Controleer uw e-mailadres.");
      return;
    }
    if (uploading) {
      toast.error("Even geduld, uw foto's worden nog geüpload.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quote`, formData);
      toast.success("Uw offerte aanvraag is verzonden! U ontvangt een bevestiging per e-mail en wij nemen binnen 24 uur contact met u op.");
      setFormData({ name: "", email: "", phone: "", address: "", service: "", preferred_date: "", is_spoed: false, message: "", photos: [] });
      setPhotoFiles([]);
    } catch {
      toast.error("Er is iets misgegaan. Probeer het opnieuw of bel ons op 06 85 07 10 09.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO
        title="Contact | Offerte Aanvragen - Cardoso Bouw en Onderhoud Breukelen"
        description="Neem contact op met Cardoso Bouw en Onderhoud voor een vrijblijvende offerte. Bel, WhatsApp of vul het formulier in. Aannemer in Breukelen, werkzaam in Noord-Holland, Zuid-Holland, Utrecht en Flevoland."
        keywords="contact aannemer Breukelen, offerte aanvragen bouwbedrijf, Cardoso Bouw contact"
        breadcrumb="Contact"
      />
      {/* Hero */}
      <section className="relative h-[40vh] min-h-[320px] flex items-end bg-[#005F5F]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pb-16 w-full">
          <p className="text-[#C5A059] font-bold uppercase tracking-[0.2em] text-sm mb-4">
            Contact
          </p>
          <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white uppercase leading-tight">
            Neem contact op
          </h1>
          <p className="text-white/70 text-base mt-4 max-w-lg">
            Vraag een vrijblijvende offerte aan of stel uw vraag. Wij reageren binnen 24 uur.
          </p>
        </div>
      </section>

      {/* Form + Contact Info */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            {/* Form */}
            <div className="lg:col-span-7">
              <div className="flex items-center gap-4 mb-6">
                <h2 className="font-['Barlow_Condensed'] text-2xl sm:text-3xl font-bold uppercase text-[#0F172A]">
                  Offerte aanvragen
                </h2>
                <div className="inline-flex items-center gap-1.5 bg-green-500 text-white text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  Gratis & Vrijblijvend
                </div>
              </div>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="quote-form" noValidate>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="quote-name" className="block text-sm font-medium text-slate-700 mb-2">
                      Naam *
                    </label>
                    <Input
                      id="quote-name"
                      name="name"
                      autoComplete="name"
                      data-testid="quote-name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Uw naam"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="quote-email" className="block text-sm font-medium text-slate-700 mb-2">
                      Email *
                    </label>
                    <Input
                      id="quote-email"
                      name="email"
                      autoComplete="email"
                      inputMode="email"
                      data-testid="quote-email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="uw@email.nl"
                      className={inputClass}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="quote-phone" className="block text-sm font-medium text-slate-700 mb-2">
                      Telefoon *
                    </label>
                    <Input
                      id="quote-phone"
                      name="phone"
                      autoComplete="tel"
                      inputMode="tel"
                      data-testid="quote-phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="06 12345678"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="quote-address" className="block text-sm font-medium text-slate-700 mb-2">
                      Adres *
                    </label>
                    <Input
                      id="quote-address"
                      name="address"
                      autoComplete="street-address"
                      data-testid="quote-address"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      placeholder="Straat, huisnummer, postcode, plaats"
                      className={inputClass}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="quote-service" className="block text-sm font-medium text-slate-700 mb-2">
                      Dienst *
                    </label>
                    <Select
                      value={formData.service}
                      onValueChange={(val) => setFormData({ ...formData, service: val })}
                    >
                      <SelectTrigger
                        id="quote-service"
                        data-testid="quote-service"
                        className={inputClass}
                      >
                        <SelectValue placeholder="Kies een dienst" />
                      </SelectTrigger>
                      <SelectContent>
                        {services.map((s) => (
                          <SelectItem key={s} value={s} data-testid={`service-option-${s}`}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label htmlFor="quote-date" className="block text-sm font-medium text-slate-700 mb-2">
                      Gewenste datum *
                    </label>
                    <Input
                      id="quote-date"
                      name="preferred_date"
                      data-testid="quote-date"
                      type="date"
                      value={formData.preferred_date}
                      onChange={(e) => setFormData({ ...formData, preferred_date: e.target.value })}
                      min={new Date().toISOString().split("T")[0]}
                      className={inputClass}
                    />
                  </div>
                </div>

                {/* Spoedklus toggle */}
                <div
                  data-testid="quote-spoed-toggle"
                  role="checkbox"
                  aria-checked={formData.is_spoed}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === " " || e.key === "Enter") {
                      e.preventDefault();
                      setFormData({ ...formData, is_spoed: !formData.is_spoed });
                    }
                  }}
                  className={`p-5 border-2 cursor-pointer transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#005F5F] ${
                    formData.is_spoed
                      ? "border-[#C5A059] bg-[#C5A059]/5"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                  onClick={() => setFormData({ ...formData, is_spoed: !formData.is_spoed })}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-6 h-6 border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                      formData.is_spoed ? "border-[#C5A059] bg-[#C5A059]" : "border-slate-300"
                    }`}>
                      {formData.is_spoed && (
                        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Zap className="w-4 h-4 text-[#C5A059]" />
                        <span className="font-bold text-sm text-[#0F172A]">Spoedklus – binnen 24 uur geholpen</span>
                      </div>
                      <p className="text-slate-500 text-xs mt-1">
                        Uurtarief €75 per uur excl. btw. Wij plannen uw klus met voorrang in.
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <label htmlFor="quote-message" className="block text-sm font-medium text-slate-700 mb-2">
                    Bericht
                  </label>
                  <Textarea
                    id="quote-message"
                    name="message"
                    data-testid="quote-message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Vertel ons over uw project..."
                    rows={5}
                    className="border-slate-300 focus:border-[#005F5F] focus:ring-[#005F5F] rounded-none"
                  />
                </div>

                {/* Photo Upload */}
                <div>
                  <label htmlFor="quote-photo-input" className="block text-sm font-medium text-slate-700 mb-2">
                    Foto's toevoegen (max {MAX_PHOTOS}, tot 50MB per foto)
                  </label>
                  <input
                    id="quote-photo-input"
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotoSelect}
                    className="hidden"
                    data-testid="quote-photo-input"
                  />
                  
                  {/* Photo preview grid */}
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 mb-3" data-testid="quote-photo-grid">
                    {photoFiles.map((file, index) => (
                      <div key={file.url || index} className="relative aspect-square bg-slate-100 border border-slate-200" data-testid={`quote-photo-preview-${index}`}>
                        <img
                          src={file.preview}
                          alt={file.name}
                          className="w-full h-full object-cover"
                        />
                        {file.size && (
                          <span className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[10px] text-center py-0.5">
                            {formatBytes(file.size)}
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => removePhoto(index)}
                          aria-label={`Foto ${index + 1} verwijderen`}
                          data-testid={`quote-photo-remove-${index}`}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    
                    {/* Add photo button */}
                    {photoFiles.length < MAX_PHOTOS && (
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        aria-label="Foto's toevoegen"
                        className="aspect-square border-2 border-dashed border-slate-300 hover:border-[#005F5F] flex flex-col items-center justify-center gap-1 transition-colors disabled:opacity-50"
                        data-testid="quote-add-photo-btn"
                      >
                        {uploading ? (
                          <div className="w-6 h-6 border-2 border-[#005F5F] border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <Camera className="w-6 h-6 text-slate-400" />
                            <span className="text-xs text-slate-400">Toevoegen</span>
                          </>
                        )}
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-slate-500" data-testid="quote-upload-hint">
                    {uploadStatus || "Upload foto's van de huidige situatie om ons te helpen uw project beter te begrijpen. Grote foto's worden automatisch verkleind."}
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={loading || uploading}
                  data-testid="quote-submit-btn"
                  className="bg-[#C5A059] hover:bg-[#005F5F] text-white font-bold uppercase tracking-wider px-10 py-4 text-sm transition-all duration-300 hover:-translate-y-0.5 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <Send className="w-4 h-4" />
                  {loading ? "Verzenden..." : "Offerte aanvragen"}
                </button>
                <p className="text-xs text-slate-400">
                  Door te verzenden gaat u akkoord met ons <Link to="/privacy" className="underline hover:text-[#005F5F]">privacybeleid</Link>.
                </p>
              </form>
            </div>

            {/* Contact Info Sidebar */}
            <div className="lg:col-span-5">
              <div className="sticky top-28 space-y-6">
                <div className="bg-[#005F5F] p-8 text-white">
                  <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase mb-6">
                    Contactgegevens
                  </h3>
                  <div className="space-y-5">
                    <a
                      href="tel:+31685071009"
                      data-testid="contact-phone"
                      className="flex items-center gap-4 text-white/80 hover:text-[#C5A059] transition-colors"
                    >
                      <div className="w-10 h-10 bg-white/10 flex items-center justify-center flex-shrink-0">
                        <Phone className="w-5 h-5 text-[#C5A059]" />
                      </div>
                      <div>
                        <p className="text-xs text-white/50 uppercase tracking-wider">Telefoon</p>
                        <p className="text-sm font-medium">06 85 07 10 09</p>
                      </div>
                    </a>
                    <a
                      href="mailto:info@cardosobouw.nl"
                      data-testid="contact-email"
                      className="flex items-center gap-4 text-white/80 hover:text-[#C5A059] transition-colors"
                    >
                      <div className="w-10 h-10 bg-white/10 flex items-center justify-center flex-shrink-0">
                        <Mail className="w-5 h-5 text-[#C5A059]" />
                      </div>
                      <div>
                        <p className="text-xs text-white/50 uppercase tracking-wider">Email</p>
                        <p className="text-sm font-medium">info@cardosobouw.nl</p>
                      </div>
                    </a>
                    <div className="flex items-start gap-4 text-white/80">
                      <div className="w-10 h-10 bg-white/10 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-5 h-5 text-[#C5A059]" />
                      </div>
                      <div>
                        <p className="text-xs text-white/50 uppercase tracking-wider">Adres</p>
                        <p className="text-sm font-medium">
                          Merwedeweg 5C1<br />
                          3621LP Breukelen
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-white/80">
                      <div className="w-10 h-10 bg-white/10 flex items-center justify-center flex-shrink-0">
                        <Clock className="w-5 h-5 text-[#C5A059]" />
                      </div>
                      <div>
                        <p className="text-xs text-white/50 uppercase tracking-wider">Bereikbaar</p>
                        <p className="text-sm font-medium">Ma - Za: 07:00 - 18:00</p>
                      </div>
                    </div>
                  </div>
                </div>

                <a
                  href="https://wa.me/31685071009"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp"
                  className="flex items-center justify-center gap-3 w-full py-4 bg-[#25D366] text-white font-bold uppercase tracking-wider text-sm transition-all duration-300 hover:bg-[#1da851]"
                >
                  <MessageCircle className="w-5 h-5" />
                  Direct WhatsApp contact
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* GOOGLE MAPS */}
      <section className="py-0" data-testid="contact-map">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2444.5!2d4.9892!3d52.1722!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sMerwedeweg+5C1%2C+3621+LP+Breukelen!5e0!3m2!1snl!2snl!4v1"
          width="100%"
          height="400"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Cardoso Bouw en Onderhoud locatie - Breukelen"
          className="w-full"
        />
      </section>
    </div>
  );
}
